import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send, Loader2, Leaf } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WELCOME = {
  role: 'assistant',
  text: "Hi! I'm the IIY Phytoventra assistant. Ask me about BCM-95® or our range, delivery, or finding the right product for your goal. 🌿",
};

const STARTERS = [
  'What is BCM-95®?',
  'How do I take it?',
  'Price, delivery & returns?',
  'Is it vegan & allergen-free?',
  'Which product suits my goal?',
  'Book a free herbalist call',
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, open]);

  const send = async (e, override) => {
    e?.preventDefault();
    const text = (override ?? input).trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { role: 'user', text }]);
    setInput('');
    setBusy(true);
    try {
      const res = await axios.post(`${API}/chat`, { message: text, session_id: sessionId });
      setSessionId(res.data.session_id);
      setMessages((m) => [...m, { role: 'assistant', text: res.data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: 'assistant', text: "Sorry, I'm having trouble right now — please try again shortly, or email support@iiypv.com." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open chat assistant"
          data-testid="chat-open-button"
          className="fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full bg-emerald-800 hover:bg-emerald-900 text-white shadow-xl flex items-center justify-center transition-transform hover:scale-105"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {open && (
        <div
          data-testid="chat-window"
          className="fixed bottom-5 right-5 z-[60] w-[90vw] max-w-sm h-[70vh] max-h-[560px] bg-white rounded-3xl shadow-2xl border border-emerald-900/10 flex flex-col overflow-hidden"
        >
          <div className="bg-emerald-800 text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 font-semibold text-sm"><Leaf className="w-4 h-4" /> IIY Phytoventra Assistant</div>
            <button onClick={() => setOpen(false)} aria-label="Close chat" data-testid="chat-close-button" className="text-white/80 hover:text-white"><X className="w-5 h-5" /></button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-stone-50" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.role === 'user' ? 'bg-emerald-800 text-white rounded-br-md' : 'bg-white border border-stone-200 text-stone-800 rounded-bl-md'}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start"><div className="bg-white border border-stone-200 rounded-2xl px-3.5 py-2.5"><Loader2 className="w-4 h-4 animate-spin text-emerald-700" /></div></div>
            )}
            <div ref={endRef} />
          </div>

          <form onSubmit={send} className="p-3 border-t border-stone-200 bg-white">
            {messages.length === 1 && !busy && (
              <div className="flex flex-wrap gap-2 mb-3" data-testid="chat-starters">
                {STARTERS.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => send(null, s)}
                    data-testid={`chat-starter-${i}`}
                    className="text-xs border border-emerald-200 text-emerald-800 bg-emerald-50 hover:bg-emerald-100 rounded-full px-3 py-1.5 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about our products…"
                data-testid="chat-input"
                className="flex-1 border-2 border-stone-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-emerald-500"
              />
              <button type="submit" disabled={busy || !input.trim()} data-testid="chat-send-button" className="w-10 h-10 rounded-xl bg-emerald-800 hover:bg-emerald-900 disabled:opacity-50 text-white flex items-center justify-center shrink-0">
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] text-stone-400 mt-2 text-center">Not medical advice. Food supplements are not a substitute for a balanced diet.</p>
          </form>
        </div>
      )}
    </>
  );
}
